import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { IPaymentWithOrder, PaymentType } from '@/types/payment';
import { PaymentGroupType } from '@/types/payment-groups';
import { UserRole } from '@/types/user';
import { getRouteApi } from '@tanstack/react-router';
import {
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
} from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { ConfirmPaymentsFormValues } from '../components/ConfirmPaymentsModal/ConfirmPaymentsModal';
import {
  PaymentPageStage,
  PaymentPageTab,
  PaymentsSearchParams,
} from '../types';
import { usePaymentRetry } from './usePaymentRetry';
import { usePaymentsCounts } from './usePaymentsCounts';
import { usePaymentsData } from './usePaymentsData';
import { usePaymentsExport } from './usePaymentsExport';

const routeApi = getRouteApi('/_admin/payments/');

type UsePaymentsListProps = {
  paymentGroupType?: PaymentGroupType;
  paymentType?: PaymentType;
  tab: PaymentPageTab;
};

export function usePaymentsList({
  paymentGroupType,
  paymentType,
  tab,
}: UsePaymentsListProps) {
  const search = routeApi.useSearch() as PaymentsSearchParams;
  const navigate = routeApi.useNavigate();
  const { currentUser } = useCurrentUserContext() ?? {};

  const [stage, setStage] = useState<PaymentPageStage>(
    () => search.stage || PaymentPageStage.ALL,
  );

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    const filters: ColumnFiltersState = [];

    if (search.status) {
      filters.push({
        id: 'status',
        value: Array.isArray(search.status) ? search.status : [search.status],
      });
    }

    // Add educationProvider filter only for TO_PAY tab
    if (tab === PaymentPageTab.TO_PAY && search.educationProvider) {
      filters.push({
        id: 'educationProvider',
        value: Array.isArray(search.educationProvider)
          ? search.educationProvider
          : [search.educationProvider],
      });
    }

    return filters;
  });

  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: (search.page ?? 1) - 1 || 0,
    pageSize: search.limit || 10,
  });

  const { countsByStage, isLoadingCounts } = usePaymentsCounts({
    paymentGroupType,
    paymentType,
  });

  const {
    exportPayments,
    exportPaymentGroups,
    isExportingPayments,
    isExportingPaymentGroups,
  } = usePaymentsExport(
    [
      ...columnFilters,
      { id: 'type', value: paymentType },
      { id: 'paymentGroupType', value: paymentGroupType },
    ],
    stage,
  );

  const {
    total,
    isLoading,
    shouldRenderPaymentsTable,
    payments,
    paymentGroups,
  } = usePaymentsData(
    stage,
    [
      ...columnFilters,
      { id: 'type', value: paymentType },
      { id: 'paymentGroupType', value: paymentGroupType },
    ],
    paginationState,
  );

  const {
    paymentGroupToRetry,
    setPaymentGroupToRetry,
    handleRetryPayment,
    closeRetryModal,
  } = usePaymentRetry();

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = updater => {
    const newFilters =
      typeof updater === 'function' ? updater(columnFilters) : updater;
    setColumnFilters(newFilters);
  };

  const handlePaginationChange: OnChangeFn<PaginationState> = updater => {
    const newPagination =
      typeof updater === 'function' ? updater(paginationState) : updater;
    setPaginationState(newPagination);
  };

  const handleStageChange = async (newStage: PaymentPageStage) => {
    setStage(newStage);
    setColumnFilters([]);
    setPaginationState({
      pageIndex: 0,
      pageSize: paginationState.pageSize,
    });
  };

  const handleExport = () => {
    if (shouldRenderPaymentsTable) {
      exportPayments();
    } else {
      exportPaymentGroups();
    }
  };

  const handleRetryModalClose = async (
    formValues?: ConfirmPaymentsFormValues,
  ) => {
    if (formValues && paymentGroupToRetry) {
      const { paymentMethod } = formValues;
      await handleRetryPayment(paymentGroupToRetry._id, paymentMethod);
    }
    closeRetryModal();
  };

  useEffect(() => {
    const newSearch = [
      ['stage', stage ?? PaymentPageStage.ALL],
      ['page', paginationState.pageIndex + 1],
      ['limit', paginationState.pageSize],
      ['tab', tab],
      ...columnFilters.map(({ id, value }) => [id, value]),
    ].reduce(
      (acc, [key, value]) => {
        const hasMeaningfulValue = Array.isArray(value)
          ? value.length > 0
          : !!value;

        if (hasMeaningfulValue) {
          acc[key as string] = value;
        }

        return acc;
      },
      {} as Record<string, unknown>,
    );

    navigate({ search: () => newSearch });
  }, [stage, columnFilters, paginationState, navigate, search, tab]);

  const currentStage = stage || PaymentPageStage.ALL;
  const isExporting = shouldRenderPaymentsTable
    ? isExportingPayments
    : isExportingPaymentGroups;

  const mainRole = currentUser?.roles?.find(role =>
    [
      UserRole.EducationProvider,
      UserRole.Employer,
      UserRole.TaxBenefitsProvider,
    ].includes(role),
  );

  return {
    // State
    stage: currentStage,
    columnFilters,
    paginationState,
    paymentGroupToRetry,
    tab,

    // Data
    countsByStage,
    isLoadingCounts,
    total,
    isLoading,
    shouldRenderPaymentsTable,
    payments: payments as IPaymentWithOrder[],
    paymentGroups,
    isExporting,
    mainRole,

    // Handlers
    handleColumnFiltersChange,
    handlePaginationChange,
    handleStageChange,
    handleExport,
    handleRetryModalClose,
    setPaymentGroupToRetry,
  };
}
